import {Popup, PopupTrigger} from '@mescius/wijmo.input';
import {createElement, format} from '@mescius/wijmo';
import {StringUtils, trimIndent} from './strings';
import {Button, TextInput} from '@fluentui/web-components';
import {AppComponent} from '../app.component';
import {Stdlib} from './stdlib';

export enum DialogButton {
    'Ok' = 'Ok',
    'Cancel' = 'Cancel',
    'Yes' = 'Yes',
    'No' = 'No',
    'Close' = 'Close'
}

export interface AlertDialogOptions {
    owner?: HTMLElement;
    error?: boolean;
    header?: string;
    iconHtml?: string;
    body?: string;
    bodyHtml?: string;
    big?: boolean;
    faIconCls?: string;
    buttons?: DialogButton[];
    height?: string;
    width?: string;
    initialValue?: any;
    onPopupCreated?: (popup: Popup) => void;
    onBeforeClose?: (popup: Popup) => void;
}

export interface AlertShareLinkOptions extends AlertDialogOptions {
    link: string;
    title?: string;
}

export class Alerts {

    static createDialog(content: any, input = false) {
        // create dialog
        const template = `<div class="fullsize-vbox wijmo-dialog">
                <div class="dialog-header modal-header">
                  <h2>{icon} <span class="placeholder-dialog-title">{title}</span></h2>
                </div>
                <div class="dialog-content layout-vbox">
                    {body}
                </div>
                <div class="dialog-footer button-hbox">
                    {footer}
                </div>
            </div>
`

        const dialog = createElement(format(template, content));
        return dialog;
    }

    static async alertPopup(options: AlertDialogOptions): Promise<DialogButton> {
        const iconCls = options.iconHtml ?? (options.error ? 'fas fa-exclamation-triangle' : 'fas fa-info-circle');
        const dialog = Alerts.createDialog({
            body: options.body ? StringUtils.escapeHtml(options.body) : options.bodyHtml || '',
            title: StringUtils.escapeHtml(options.header) || 'Alert',
            icon: `<i class="${iconCls}"></i>`,
            footer: Alerts.buttons(...options.buttons ?? [DialogButton.Close])
        });

        dialog.style.height = options.height ?? (options.big ? '300px' : '200px');
        dialog.style.width = options.width ?? (options.big ? '600px' : '300px');

        const hiding = (sender: Popup, e: any) => {
            try { options.onBeforeClose?.call(undefined, popup); }
            catch (e) { console.error('Error in onBeforeClose callback:', e); }
        };

        const popup = new Popup(dialog, {
            owner: options.owner,
            showTrigger: PopupTrigger.None,
            hideTrigger: PopupTrigger.None,
            isDraggable: true,
            isResizable: true,
            fadeIn: false,
            fadeOut: false,
            hiding: hiding,
            // hidden: () => popup.dispose()
        });

        options.onPopupCreated?.call(undefined, popup);

        return new Promise<DialogButton>((resolve) => {
            popup.show(true, (sender: { dialogResult: any }) => {
                // dialogResult is a string starting with 'wj-hide-' followed by the button name
                const buttonName = sender.dialogResult?.replace('wj-hide-', '');
                const button = buttonName as DialogButton ?? DialogButton.Cancel;

                resolve(button);
            });
        });
    }

    private static buttons(... buttons: DialogButton[]): string {
        const fluentButtons = buttons.map(b => Alerts.fluentButton(`wj-hide-${b}`, b));
        return fluentButtons.join('');
    }

    private static fluentButton(cls: string, text: string): string {
        return `<fluent-button class="${cls}">${text}</fluent-button>`;
    }

    static async alertHttpErrorPopup(options: AlertDialogOptions): Promise<DialogButton> {
        const body = createElement('<div><h4>An Error Occurred</h4><pre></pre></div>');
        const pre = body.querySelector('pre')!;
        pre.style.whiteSpace = 'break-spaces';
        pre.textContent = options.body || 'An unexpected error occurred. Please try again later.';
        return Alerts.alertPopup({ bodyHtml: body.outerHTML, big: true });
    }

    static async alertPrompt(options: AlertDialogOptions): Promise<string | undefined> {
        const body = createElement(trimIndent`
            <div>${options.body}</div>
            <fluent-text-input style="width: 95%; align-self: center"></fluent-text-input>
        `);
        body.classList.add('fullsize-vbox');
        body.style.gap = '10px';

        let returnedValue: string | undefined;
        const onBeforeClose = (popup: Popup) => {
            const input = popup.hostElement.querySelector('fluent-text-input') as TextInput;
            returnedValue = input.value ?? undefined
        };

        const onPopupCreated = (popup: Popup) => {
            const input = popup.hostElement.querySelector('fluent-text-input') as TextInput;
            input.value = options.initialValue ?? '';
        }

        const btn = await Alerts.alertPopup({
            bodyHtml: body.outerHTML,
            width: '400px',
            height: '180px',
            buttons: [DialogButton.Ok, DialogButton.Cancel],
            onPopupCreated,
            onBeforeClose
        });

        return (btn === DialogButton.Ok) ? returnedValue : undefined;
    }

    static async alertPromptTextArea(options: AlertDialogOptions): Promise<string | undefined> {
        const body = createElement(trimIndent`
            <div>${options.body}</div>
            <textarea style="width: 95%; align-self: center; height: 160px; resize: none"></textarea>
        `);
        body.classList.add('fullsize-vbox');
        body.style.gap = '10px';

        let returnedValue: string | undefined;
        const onBeforeClose = (popup: Popup) => {
            const textArea = popup.hostElement.querySelector('textarea') as HTMLTextAreaElement
            return returnedValue = textArea.value ?? undefined;
        };

        const onPopupCreated = (popup: Popup) => {
            const input = popup.hostElement.querySelector('textarea') as HTMLTextAreaElement;
            input.value = options.initialValue?.toString() ?? '';
        }

        const btn = await Alerts.alertPopup({
            bodyHtml: body.outerHTML,
            width: '400px',
            height: '260px',
            buttons: [DialogButton.Ok, DialogButton.Cancel],
            onPopupCreated,
            onBeforeClose
        });

        return (btn === DialogButton.Ok) ? returnedValue : undefined;
    }

    static async alertPromptNumeric(options: AlertDialogOptions): Promise<number | undefined> {
        const body = createElement(trimIndent`
            <div>${options.body}</div>
            <fluent-text-input style="width: 95%; align-self: center" type="number" min="1"></fluent-text-input>
        `);
        body.classList.add('fullsize-vbox');
        body.style.gap = '10px';

        let returnedValue: string | undefined;
        const onBeforeClose = (popup: Popup) => {
            const input = popup.hostElement.querySelector('fluent-text-input') as TextInput;
            returnedValue = input.value ?? undefined
        };

        const onPopupCreated = (popup: Popup) => {
            const input = popup.hostElement.querySelector('fluent-text-input') as TextInput;
            setTimeout(() => input.value = options.initialValue?.toString() ?? '');
        }

        const btn = await Alerts.alertPopup({
            bodyHtml: body.outerHTML,
            width: '400px',
            height: '180px',
            buttons: [DialogButton.Ok, DialogButton.Cancel],
            onPopupCreated,
            onBeforeClose
        });

        return (btn === DialogButton.Ok) ? Stdlib.let(returnedValue, it => Number.parseFloat(it)) : undefined;
    }

    static async alertShareLink(options: AlertShareLinkOptions): Promise<DialogButton> {
        const linkAsString = StringUtils.escapeJsString(options.link);
        const body = createElement(trimIndent`
            <div>${options.title}
                <fluent-button class="copy-button"><i class="far fa-paste"></i></fluent-button>
            </div>
        `);
        const btn = body.querySelector('fluent-button')! as Button;
        btn.onclick = async () => await navigator.clipboard.writeText(options.link);

        const popupCallback = (sender: Popup) => {
            const btn = sender.hostElement.querySelector('fluent-button.copy-button')! as Button;
            btn.onclick = async () => {
                await navigator.clipboard.writeText(options.link);
                AppComponent.messageToast.show({ message: 'Link copied to clipboard!' });
            }
        };

        return Alerts.alertPopup({
            ...options,
            onPopupCreated: popupCallback,
            bodyHtml: body.outerHTML
        });
    }

    static showProgressDialog(text: string): Popup {
        const dialog = Alerts.createDialog({
            body: `<div class="layout-vbox" style="align-items: center; gap: 10px">
                        ${text}<br>
                        <fluent-progress-ring></fluent-progress-ring>
                   </div>`,
            title: 'Please wait...',
            icon: `<i class="far fa-hourglass"></i>`,
            footer: Alerts.buttons(DialogButton.Cancel)
        });

        dialog.style.height = '180px';
        dialog.style.width = '250px';

        const popup = new Popup(dialog, {
            showTrigger: PopupTrigger.None,
            hideTrigger: PopupTrigger.None,
            isDraggable: true,
            isResizable: true,
            fadeIn: false,
            fadeOut: false
        });


        popup.show(true, (sender: { dialogResult: any }) => {})

        return popup;
    }
}
